/* This module provides access to the Ampli instance (Amplitude).

Tracking might be enabled or disabled overall for an app for privacy, so we should only call
tracking events by using the useWithAmpi hook provided here. Outside of any context it will
give direct access to the Ampli instance, however inside the AmpliContextProvider it will
silence calls if tracking is not set up.
*/
import { Ampli, ampli as rawAmpli } from '@ampli'
import { createContext, useCallback, useContext } from 'react'

interface Props {
	ampli?: Ampli
	children?: React.ReactNode
}

interface Values {
	withAmpli: (fn: (ampli: Ampli) => void) => void
}

const defaultValues: Values = {
	// By default (if no context provided), access the raw ampli instance.
	withAmpli: (fn: (ampli: Ampli) => void) => {
		fn(rawAmpli)
	},
}

const AmpliContext = createContext(defaultValues)

const AmpliContextProvider = ({ ampli, children }: Props) => {
	const withAmpli = useCallback(
		(fn: (ampli: Ampli) => void) => {
			if (ampli) {
				fn(ampli)
			}
		},
		[ampli]
	)

	return (
		<AmpliContext.Provider value={{ withAmpli }}>
			{children}
		</AmpliContext.Provider>
	)
}

export const useWithAmpli = () => {
	const { withAmpli } = useContext(AmpliContext)
	return withAmpli
}

export default AmpliContextProvider
